/**
 * Technology Remove Tag Modal Component
 *
 * Migrated from Drawer to shared RemoveTag component (2025-01-19)
 * Follows the same pattern as Assets section for UI consistency.
 *
 * Changes from original:
 * - Replaced custom Drawer implementation with shared RemoveTag component
 * - Added proper backend API integration with axios
 * - Implemented query invalidation for real-time updates
 * - Added toast notifications for user feedback with accurate counts
 * - Reduced component from 195 lines to ~120 lines
 * - Uses Promise.allSettled for graceful partial failure handling
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { RemoveTag } from '@/sections/RemoveTag';
import { useMutation } from '@/utils/api';
import { tagRemovedError, tagRemovedSuccess } from '@/utils/tagToastMessages';

interface Props {
  open: boolean;
  onClose: () => void;
  technologyKeys: string[];
  onSuccess?: () => void;
}

/**
 * Modal component for removing tags from technologies
 *
 * Uses shared RemoveTag component for consistent UX across the platform.
 * Handles both single technology (from drawer) and bulk operations (from table).
 *
 * Features:
 * - Auto-fetches common tags across selected technologies
 * - Visual tag selection interface
 * - Graceful handling of partial failures
 * - Accurate success/failure reporting
 *
 * Example usage:
 * ```tsx
 * <TechnologyRemoveTag
 *   open={modalState.removeTagModal}
 *   onClose={modalHandlers.closeRemoveTagModal}
 *   technologyKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const TechnologyRemoveTag = ({
  open,
  onClose,
  technologyKeys,
  onSuccess,
}: Props) => {
  const axios = useAxios();

  const { mutateAsync: removeTag, status: removingTag } = useMutation({
    defaultErrorMessage: 'Failed to remove tag',
    mutationFn: async (tagToRemove: string) => {
      const promises = technologyKeys.map(async techKey => {
        // Fetch current technology to get both tags AND status
        const currentTech = await axios.get(`/my`, {
          params: {
            label: 'technology',
            key: techKey,
            exact: true,
          },
        });

        const tech = currentTech.data.technologies?.[0];
        const existingTags = tech?.tags || [];
        const currentStatus = tech?.status || 'A';

        // Remove the tag
        const updatedTags = existingTags.filter(
          (tag: string) => tag !== tagToRemove
        );

        const payload = {
          key: techKey,
          status: currentStatus, // Include current status (required by backend)
          tags: updatedTags,
        };

        return axios.put('/technology', payload);
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        // Use predicate-based invalidation matching Assets pattern
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
          refetchType: 'active', // Only refetch visible queries for better performance
        });
      }

      // Log failures for debugging with enhanced error details
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            technologyKey: technologyKeys[i],
            error: r.reason,
            errorResponse: r.reason?.response,
            errorData: r.reason?.response?.data,
          }));
        console.error(
          '[TechnologyRemoveTag] Failed to remove tag from technologies:',
          failedResults
        );
      }

      // Return result summary for handleSubmit to use
      return { succeeded, failed, tagToRemove };
    },
  });

  const handleSubmit = async (tag: string) => {
    try {
      const result = await removeTag(tag);

      // Show accurate feedback based on results
      if (result.succeeded > 0 && result.failed === 0) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagRemovedSuccess('technology', count));
      } else if (result.succeeded > 0 && result.failed > 0) {
        toast.warning(
          `Tag "${result.tagToRemove}" removed from ${result.succeeded} ${result.succeeded > 1 ? 'technologies' : 'technology'}, ` +
            `but failed for ${result.failed} ${result.failed > 1 ? 'technologies' : 'technology'}`
        );
      } else {
        toast.error(
          `Failed to remove tag "${result.tagToRemove}" from ${result.failed} ${result.failed > 1 ? 'technologies' : 'technology'}`
        );
      }

      onClose();
      onSuccess?.();
    } catch (error: any) {
      console.error('[TechnologyRemoveTag] Tag removal failed:', error);
      toast.error(
        tagRemovedError(
          'technology',
          error.response?.data?.message || error.message
        )
      );
    }
  };

  return (
    <RemoveTag
      open={open}
      onClose={onClose}
      disabled={removingTag === 'pending'}
      onSubmit={handleSubmit}
      assetKeys={technologyKeys}
      label="technology"
    />
  );
};
